/**
 * @module services/zoteroService
 * @description Zotero API service for Preparo bibliography integration
 *
 * Provides read-only access to Zotero libraries, collections, and items
 * via the Spectral Cloud Portal proxy (no direct API key handling).
 */

import { proxyZoteroRequest } from './supabaseClient';
import { createLogger } from './loggerService';

const log = createLogger('zotero');

// =============================================================================
// TYPES
// =============================================================================

/** Library type */
export type LibraryType = 'user' | 'group';

/** Stored library reference */
export interface StoredLibrary {
  id: number;
  type: LibraryType;
  name: string;
}

/** Zotero group */
export interface ZoteroGroup {
  id: number;
  version: number;
  data: {
    id: number;
    name: string;
    description: string;
    owner: number;
    type: 'PublicOpen' | 'PublicClosed' | 'Private';
  };
}

/** Zotero collection */
export interface ZoteroCollection {
  key: string;
  version: number;
  data: {
    key: string;
    name: string;
    parentCollection: string | false;
  };
  children?: ZoteroCollection[];
  level?: number;
}

/** Zotero item (with all fields used for CSL conversion) */
export interface ZoteroItem {
  key: string;
  version: number;
  data: {
    key: string;
    itemType: string;
    title: string;
    dateModified?: string; // ISO date string of last modification on Zotero
    dateAdded?: string;
    creators?: Array<{
      creatorType: string;
      firstName?: string;
      lastName?: string;
      name?: string;
    }>;
    date?: string;
    publicationTitle?: string;
    bookTitle?: string;
    publisher?: string;
    place?: string;
    volume?: string;
    issue?: string;
    pages?: string;
    numPages?: string;
    edition?: string;
    series?: string;
    seriesNumber?: string;
    ISBN?: string;
    ISSN?: string;
    DOI?: string;
    url?: string;
    abstractNote?: string;
    language?: string;
    extra?: string;
  };
}

/** API key validation result */
export interface ApiKeyValidation {
  userID: number;
  username: string;
  access?: {
    user?: { library?: boolean; write?: boolean };
    groups?: { all?: { library?: boolean; write?: boolean } };
  };
}

/** Service result type */
export type ServiceResult<T> =
  | { success: true; data: T }
  | { success: false; error: string };

// =============================================================================
// API HELPERS
// =============================================================================

/**
 * Build library endpoint path
 */
function buildLibraryEndpoint(libraryId: number, libraryType: LibraryType): string {
  return libraryType === 'user' ? `/users/${libraryId}` : `/groups/${libraryId}`;
}

/**
 * Format creators for display (e.g., "Smith, J., & Doe, A.")
 */
export function formatCreators(
  creators?: ZoteroItem['data']['creators'],
  maxAuthors: number = 3
): string {
  if (!creators || creators.length === 0) return '';

  const formatted = creators.slice(0, maxAuthors).map((c) => {
    if (c.name) return c.name;
    if (c.lastName && c.firstName) {
      return `${c.lastName}, ${c.firstName.charAt(0)}.`;
    }
    return c.lastName || c.firstName || '';
  });

  let result = formatted.join(', ');
  if (creators.length > maxAuthors) {
    result += ' et al.';
  }

  return result;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Validate API key and get user info
 */
export async function validateApiKey(): Promise<ServiceResult<ApiKeyValidation>> {
  try {
    log.debug('Validating API key');

    const response = await proxyZoteroRequest<ApiKeyValidation>({
      path: '/keys/current',
      method: 'GET',
    });

    // Check if response has the expected data
    if (!response.data || typeof response.data.userID !== 'number') {
      log.error('Invalid API key response', { response });
      return { success: false, error: 'Invalid response from Zotero API' };
    }

    log.info('API key validated', { userId: response.data.userID });
    return { success: true, data: response.data };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to validate API key';
    log.error('API key validation failed', { error: message });
    return { success: false, error: message };
  }
}

/**
 * Get user's group libraries
 */
export async function getGroups(userId: number): Promise<ServiceResult<ZoteroGroup[]>> {
  try {
    log.debug('Fetching groups', { userId });

    const response = await proxyZoteroRequest<ZoteroGroup[]>({
      path: `/users/${userId}/groups`,
      method: 'GET',
    });

    log.info('Groups fetched', { count: response.data?.length || 0 });
    return { success: true, data: response.data || [] };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch groups';
    log.error('Failed to fetch groups', { error: message });
    return { success: false, error: message };
  }
}

/**
 * Get collections for a library
 */
export async function getCollections(
  libraryId: number,
  libraryType: LibraryType,
  options: { signal?: AbortSignal } = {}
): Promise<ServiceResult<ZoteroCollection[]>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    log.debug('Fetching collections', { libraryId, libraryType });

    const response = await proxyZoteroRequest<ZoteroCollection[]>({
      path: `${endpoint}/collections`,
      method: 'GET',
      params: { limit: '100' },
      signal: options.signal,
    });

    log.info('Collections fetched', { count: response.data?.length || 0 });
    return { success: true, data: response.data || [] };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return { success: false, error: 'Request aborted' };
    }
    const message = error instanceof Error ? error.message : 'Failed to fetch collections';
    log.error('Failed to fetch collections', { error: message });
    return { success: false, error: message };
  }
}

/**
 * Get items in a collection
 */
export async function getCollectionItems(
  libraryId: number,
  libraryType: LibraryType,
  collectionKey: string,
  options: { limit?: number; start?: number; signal?: AbortSignal } = {}
): Promise<ServiceResult<{ items: ZoteroItem[]; totalResults: number }>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    const { limit = 50, start = 0, signal } = options;

    log.debug('Fetching collection items', { libraryId, libraryType, collectionKey });

    const response = await proxyZoteroRequest<ZoteroItem[]>({
      path: `${endpoint}/collections/${collectionKey}/items`,
      method: 'GET',
      params: {
        limit: String(limit),
        start: String(start),
        itemType: '-attachment || note', // Exclude attachments (Zotero filter format)
        sort: 'creator',
        direction: 'asc',
      },
      signal,
    });

    log.info('Collection items fetched', {
      count: response.data?.length || 0,
      total: response.headers.totalResults,
    });

    return {
      success: true,
      data: {
        items: response.data || [],
        totalResults: response.headers.totalResults || response.data?.length || 0,
      },
    };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return { success: false, error: 'Request aborted' };
    }
    const message = error instanceof Error ? error.message : 'Failed to fetch items';
    log.error('Failed to fetch collection items', { error: message });
    return { success: false, error: message };
  }
}

/**
 * Get specific items by their keys
 * Used to resolve citations in a document without loading the full library
 */
export async function getItemsByKeys(
  libraryId: number,
  libraryType: LibraryType,
  itemKeys: string[],
  options: { signal?: AbortSignal } = {}
): Promise<ServiceResult<ZoteroItem[]>> {
  if (itemKeys.length === 0) {
    return { success: true, data: [] };
  }

  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    const { signal } = options;

    // Zotero API accepts comma-separated item keys
    const keysParam = itemKeys.join(',');

    log.debug('Fetching items by keys', { libraryId, libraryType, keyCount: itemKeys.length });

    const response = await proxyZoteroRequest<ZoteroItem[]>({
      path: `${endpoint}/items`,
      method: 'GET',
      params: {
        itemKey: keysParam,
        limit: String(itemKeys.length),
      },
      signal,
    });

    log.info('Items by keys fetched', {
      requested: itemKeys.length,
      returned: response.data?.length || 0,
    });

    return { success: true, data: response.data || [] };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return { success: false, error: 'Request aborted' };
    }
    const message = error instanceof Error ? error.message : 'Failed to fetch items by keys';
    log.error('Failed to fetch items by keys', { error: message });
    return { success: false, error: message };
  }
}

/**
 * Get all items in a library (no collection filter)
 */
export async function getLibraryItems(
  libraryId: number,
  libraryType: LibraryType,
  options: { limit?: number; start?: number; signal?: AbortSignal } = {}
): Promise<ServiceResult<{ items: ZoteroItem[]; totalResults: number }>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    const { limit = 50, start = 0, signal } = options;

    log.debug('Fetching library items', { libraryId, libraryType });

    const response = await proxyZoteroRequest<ZoteroItem[]>({
      path: `${endpoint}/items`,
      method: 'GET',
      params: {
        limit: String(limit),
        start: String(start),
        itemType: '-attachment || note', // Exclude attachments (Zotero filter format)
        sort: 'creator',
        direction: 'asc',
      },
      signal,
    });

    log.info('Library items fetched', {
      count: response.data?.length || 0,
      total: response.headers.totalResults,
    });

    return {
      success: true,
      data: {
        items: response.data || [],
        totalResults: response.headers.totalResults || response.data?.length || 0,
      },
    };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return { success: false, error: 'Request aborted' };
    }
    const message = error instanceof Error ? error.message : 'Failed to fetch items';
    log.error('Failed to fetch library items', { error: message });
    return { success: false, error: message };
  }
}

// =============================================================================
// COLLECTION TREE HELPERS
// =============================================================================

/**
 * Build a tree structure from flat collections list
 */
export function buildCollectionTree(collections: ZoteroCollection[]): ZoteroCollection[] {
  const map = new Map<string, ZoteroCollection>();
  const roots: ZoteroCollection[] = [];

  // First pass: create map
  for (const collection of collections) {
    map.set(collection.key, { ...collection, children: [], level: 0 });
  }

  // Second pass: build tree
  for (const collection of collections) {
    const node = map.get(collection.key)!;
    const parentKey = collection.data.parentCollection;

    if (parentKey && map.has(parentKey)) {
      const parent = map.get(parentKey)!;
      parent.children = parent.children || [];
      parent.children.push(node);
      node.level = (parent.level || 0) + 1;
    } else {
      roots.push(node);
    }
  }

  // Sort children alphabetically
  const sortChildren = (nodes: ZoteroCollection[]) => {
    nodes.sort((a, b) => a.data.name.localeCompare(b.data.name));
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        sortChildren(node.children);
      }
    }
  };

  sortChildren(roots);
  return roots;
}

/**
 * Flatten tree for display with expansion state
 */
export function flattenCollectionTree(
  tree: ZoteroCollection[],
  expandedKeys: Set<string>,
  showAll = false
): ZoteroCollection[] {
  const result: ZoteroCollection[] = [];

  const traverse = (nodes: ZoteroCollection[], level: number) => {
    for (const node of nodes) {
      result.push({ ...node, level });
      if (node.children && node.children.length > 0 && (showAll || expandedKeys.has(node.key))) {
        traverse(node.children, level + 1);
      }
    }
  };

  traverse(tree, 0);
  return result;
}

// =============================================================================
// CSL TO ZOTERO CONVERSION
// =============================================================================

/** CSL name format */
interface CSLName {
  family?: string;
  given?: string;
  literal?: string;
}

/** CSL date format */
interface CSLDate {
  'date-parts'?: number[][];
  raw?: string;
  literal?: string;
}

/** Zotero item data stored in CSL item for direct updates */
interface StoredZoteroData {
  key?: string;
  version?: number;
  itemType?: string;
  title?: string;
  creators?: Array<{
    creatorType: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  }>;
  date?: string;
  dateModified?: string;
  dateAdded?: string;
  [key: string]: unknown;
}

/** CSL item (minimal for conversion) */
interface CSLItem {
  id: string;
  type: string;
  title?: string;
  author?: CSLName[];
  editor?: CSLName[];
  translator?: CSLName[];
  issued?: CSLDate;
  'container-title'?: string;
  publisher?: string;
  'publisher-place'?: string;
  page?: string;
  volume?: string;
  issue?: string;
  DOI?: string;
  URL?: string;
  /** Original Zotero data for direct API updates (avoids CSL→Zotero conversion loss) */
  _zoteroData?: StoredZoteroData;
  [key: string]: unknown;
}

/**
 * Convert CSL names to Zotero creators format
 */
function cslNamesToZoteroCreators(
  names: CSLName[] | undefined,
  creatorType: string
): ZoteroItem['data']['creators'] {
  if (!names || names.length === 0) return [];

  return names.map((name) => {
    if (name.literal) {
      return { creatorType, name: name.literal };
    }
    return {
      creatorType,
      firstName: name.given || '',
      lastName: name.family || '',
    };
  });
}

/**
 * Convert CSL date to Zotero date string
 */
function cslDateToZoteroDate(date: CSLDate | undefined): string | undefined {
  if (!date) return undefined;

  if (date.raw) return date.raw;
  if (date.literal) return date.literal;

  if (date['date-parts'] && date['date-parts'][0]) {
    const parts = date['date-parts'][0];
    if (parts.length === 1) return String(parts[0]);
    if (parts.length === 2) return `${parts[0]}-${String(parts[1]).padStart(2, '0')}`;
    if (parts.length >= 3)
      return `${parts[0]}-${String(parts[1]).padStart(2, '0')}-${String(parts[2]).padStart(2, '0')}`;
  }

  return undefined;
}

/**
 * Convert CSL item to Zotero API data format (partial update)
 */
export function cslToZoteroData(csl: CSLItem): Partial<ZoteroItem['data']> {
  const data: Partial<ZoteroItem['data']> = {};

  if (csl.title !== undefined) data.title = csl.title;

  // Combine all creator types
  const creators: ZoteroItem['data']['creators'] = [];
  if (csl.author) creators.push(...cslNamesToZoteroCreators(csl.author, 'author'));
  if (csl.editor) creators.push(...cslNamesToZoteroCreators(csl.editor, 'editor'));
  if (csl.translator) creators.push(...cslNamesToZoteroCreators(csl.translator, 'translator'));
  if (creators.length > 0) data.creators = creators;

  // Date
  const dateStr = cslDateToZoteroDate(csl.issued);
  if (dateStr !== undefined) data.date = dateStr;

  // Container title (maps to multiple Zotero fields depending on type)
  if (csl['container-title'] !== undefined) {
    data.publicationTitle = csl['container-title'];
  }

  // Other fields
  if (csl.publisher !== undefined) data.publisher = csl.publisher;
  if (csl['publisher-place'] !== undefined) data.place = csl['publisher-place'];
  if (csl.page !== undefined) data.pages = csl.page;
  if (csl.volume !== undefined) data.volume = csl.volume;
  if (csl.issue !== undefined) data.issue = csl.issue;
  if (csl.DOI !== undefined) data.DOI = csl.DOI;
  if (csl.URL !== undefined) data.url = csl.URL;

  return data;
}

/**
 * Get a single item by its key
 */
export async function getItemByKey(
  libraryId: number,
  libraryType: LibraryType,
  itemKey: string
): Promise<ServiceResult<ZoteroItem>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    log.debug('Fetching item by key', { libraryId, libraryType, itemKey });

    const response = await proxyZoteroRequest<ZoteroItem>({
      path: `${endpoint}/items/${itemKey}`,
      method: 'GET',
    });

    log.info('Item fetched', { key: itemKey, version: response.data?.version });
    return { success: true, data: response.data };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch item';
    log.error('Failed to fetch item by key', { itemKey, error: message });
    return { success: false, error: message };
  }
}

/**
 * Update an item on Zotero server
 * Fetches current version first to ensure conflict-free update
 *
 * Uses _zoteroData from CSL item if available (native format, avoids conversion loss)
 * Falls back to CSL→Zotero conversion if _zoteroData not present
 */
export async function updateItem(
  libraryId: number,
  libraryType: LibraryType,
  itemKey: string,
  cslItem: CSLItem
): Promise<ServiceResult<{ version: number }>> {
  try {
    log.debug('Updating item on Zotero', { libraryId, libraryType, itemKey });

    // First, get current item to obtain version
    const currentResult = await getItemByKey(libraryId, libraryType, itemKey);
    if (!currentResult.success) {
      const failedResult = currentResult as { success: false; error: string };
      return { success: false, error: `Failed to get current item: ${failedResult.error}` };
    }

    const currentVersion = currentResult.data.version;
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    // Use native Zotero data if available (stored during initial fetch)
    // This avoids lossy CSL→Zotero conversion
    let zoteroData: Partial<ZoteroItem['data']>;

    if (cslItem._zoteroData) {
      // Use the stored Zotero data directly (like Refero does)
      // Remove read-only fields that can't be updated
      const { key, version, dateAdded, dateModified, ...editableData } = cslItem._zoteroData;
      zoteroData = editableData as Partial<ZoteroItem['data']>;
      log.debug('Using native Zotero data for update', { itemKey, hasZoteroData: true });
    } else {
      // Fallback: convert CSL to Zotero format (may lose some data)
      zoteroData = cslToZoteroData(cslItem);
      log.warn('No _zoteroData found, using CSL conversion (may lose data)', { itemKey });
    }

    log.debug('Patching item', {
      itemKey,
      version: currentVersion,
      dataPreview: JSON.stringify(zoteroData).substring(0, 300)
    });

    // PATCH the item
    const response = await proxyZoteroRequest<{ version: number }>({
      path: `${endpoint}/items/${itemKey}`,
      method: 'PATCH',
      body: zoteroData,
      zoteroHeaders: {
        'If-Unmodified-Since-Version': String(currentVersion),
      },
    });

    log.info('Item updated on Zotero', { itemKey, newVersion: response.headers.lastModifiedVersion });

    return {
      success: true,
      data: { version: response.headers.lastModifiedVersion || currentVersion + 1 },
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update item';
    log.error('Failed to update item on Zotero', { itemKey, error: message });
    return { success: false, error: message };
  }
}

/**
 * Update an item on Zotero server using native Zotero format directly
 * This is the preferred method - no format conversion needed
 */
export async function updateItemDirect(
  libraryId: number,
  libraryType: LibraryType,
  itemKey: string,
  zoteroItemData: Record<string, unknown>
): Promise<ServiceResult<{ version: number }>> {
  try {
    log.debug('Updating item on Zotero (direct)', { libraryId, libraryType, itemKey });

    // First, get current item to obtain version
    const currentResult = await getItemByKey(libraryId, libraryType, itemKey);
    if (!currentResult.success) {
      const failedResult = currentResult as { success: false; error: string };
      return { success: false, error: `Failed to get current item: ${failedResult.error}` };
    }

    const currentVersion = currentResult.data.version;
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    // Remove read-only fields that can't be updated
    const { key, version, dateAdded, dateModified, _localModified, ...editableData } = zoteroItemData;

    log.debug('Patching item (direct)', {
      itemKey,
      version: currentVersion,
      dataPreview: JSON.stringify(editableData).substring(0, 300)
    });

    // PATCH the item
    const response = await proxyZoteroRequest<{ version: number }>({
      path: `${endpoint}/items/${itemKey}`,
      method: 'PATCH',
      body: editableData,
      zoteroHeaders: {
        'If-Unmodified-Since-Version': String(currentVersion),
      },
    });

    log.info('Item updated on Zotero (direct)', { itemKey, newVersion: response.headers.lastModifiedVersion });

    return {
      success: true,
      data: { version: response.headers.lastModifiedVersion || currentVersion + 1 },
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update item';
    log.error('Failed to update item on Zotero (direct)', { itemKey, error: message });
    return { success: false, error: message };
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export const zoteroService = {
  validateApiKey,
  getGroups,
  getCollections,
  getCollectionItems,
  getItemsByKeys,
  getLibraryItems,
  getItemByKey,
  updateItem,
  updateItemDirect,
  cslToZoteroData,
  buildCollectionTree,
  flattenCollectionTree,
  formatCreators,
};

export default zoteroService;
